import { useCallback, useEffect, useRef, useState } from "react";
import { FolderOpen, FolderSync, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { guessLogement, postWithRetry } from "@/components/MultiUploadCard";
import { nettoyerDoublonsRapports, rangerAnciensRapports, saveRapports } from "@/components/RapportButtons";
import { aControler, ImportControleDialog } from "@/components/ImportControleDialog";

const ACCEPT = /\.(csv|xlsx|xls|pdf|txt)$/i;
// Les rapports que l'application écrit elle-même dans le dossier ne sont jamais réimportés
const lisible = (nom) => ACCEPT.test(nom) && !/^Rapprochement du /i.test(nom);
const ORDRE = { banque: 0, airbnb: 1, booking: 2, facture_booking: 3 };
const LABEL = { banque: "Banque", airbnb: "Airbnb", booking: "Booking", facture_booking: "Facture Booking" };
const INFO_KEY = "rb_dossier";
const DONE_KEY = "rb_dossier_faits";
export const typeFromName = (n) => (/airbnb/i.test(n) ? "airbnb" : /booking/i.test(n) && /factur/i.test(n) && /\.pdf$/i.test(n) ? "facture_booking" : /booking/i.test(n) ? "booking" : "banque");
const nomDe = (chemin) => chemin.split("/").pop();
const dossiersDe = (chemin) => chemin.split("/").slice(0, -1).join("/");
// Nom du fichier d'abord, sinon les sous-dossiers (ex. Booking/Factures/x.pdf, OUEST/releve.csv)
const typeDe = (chemin) => (/airbnb|booking/i.test(nomDe(chemin)) ? typeFromName(nomDe(chemin)) : typeFromName(chemin));
const logementDe = (chemin) => guessLogement(nomDe(chemin)) || guessLogement(dossiersDe(chemin));
// Fichiers du dossier qui ne sont pas des relevés : factures Lodgify, notes .txt rangées avec Airbnb/Booking
const ignore = (chemin) => /lodgify/i.test(chemin) || (/\.txt$/i.test(chemin) && typeDe(chemin) !== "banque");
const sig = ({ f, chemin }) => `${chemin}|${f.size}|${f.lastModified}`;

// Range les anciens rapports de la racine puis archive les doublons d'une même période (message seulement s'il y a eu quelque chose)
async function rangerRapports(h) {
  const ranges = await rangerAnciensRapports(h).catch(() => 0);
  const archives = await nettoyerDoublonsRapports(h).catch(() => 0);
  if (ranges) toast.success(`${ranges} ancien(s) rapport(s) rangé(s) dans « ${h.name}/Rapports »`);
  if (archives) toast.success(`${archives} ancienne(s) version(s) de rapport déplacée(s) dans « Anciennes versions » : seul le plus récent de chaque période reste`);
}
const readJson = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

// Chrome / Edge : le dossier choisi est mémorisé (IndexedDB) ; Safari / Firefox : sélection du dossier à chaque fois
const idb = (mode, fn) => new Promise((resolve, reject) => {
  const req = indexedDB.open("rapproch", 1);
  req.onupgradeneeded = () => req.result.createObjectStore("kv");
  req.onerror = () => reject(req.error);
  req.onsuccess = () => {
    const r = fn(req.result.transaction("kv", mode).objectStore("kv"));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  };
});
const getHandle = () => idb("readonly", (s) => s.get("dossier")).catch(() => null);
const saveHandle = (h) => idb("readwrite", (s) => s.put(h, "dossier")).catch(() => null);

// Tous les niveaux de sous-dossiers ; chemin relatif au dossier choisi
async function filesFromHandle(handle, prefixe = "") {
  const out = [];
  for await (const entry of handle.values()) {
    if (entry.kind === "file" && lisible(entry.name)) out.push({ f: await entry.getFile(), chemin: prefixe + entry.name });
    else if (entry.kind === "directory" && !(prefixe === "" && entry.name === "Rapports")) out.push(...(await filesFromHandle(entry, `${prefixe}${entry.name}/`)));
  }
  return out;
}

export const FolderImport = ({ onDone }) => {
  const [info, setInfo] = useState(() => readJson(INFO_KEY, null));
  const [handle, setHandle] = useState(null);
  const [needsPermission, setNeedsPermission] = useState(false);
  const [busy, setBusy] = useState(false);
  const [controles, setControles] = useState([]);
  const inputRef = useRef(null);
  const forceRef = useRef(false);
  const enCours = useRef(false);
  const autoFait = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // force : renvoie aussi les fichiers déjà envoyés (doublons écartés par le serveur, logements mis à jour)
  // auto : vérification à l'ouverture de la page, silencieuse s'il n'y a rien de nouveau
  const importer = useCallback(async (files, nom, dirHandle = null, force = false, auto = false) => {
    if (enCours.current) return;
    const done = new Set(readJson(DONE_KEY, []));
    const ignores = files.filter((e) => lisible(e.f.name) && ignore(e.chemin));
    const nouveaux = files.filter((e) => lisible(e.f.name) && !ignore(e.chemin) && (force || !done.has(sig(e))))
      .sort((a, b) => ORDRE[typeDe(a.chemin)] - ORDRE[typeDe(b.chemin)]);
    const stamp = { nom, date: new Date().toISOString(), fichiers: files.length - ignores.length };
    if (ignores.length && !auto) toast.info(`${ignores.length} fichier(s) ignoré(s), ce ne sont pas des relevés : ${ignores.map((e) => e.chemin).slice(0, 4).join(" ; ")}${ignores.length > 4 ? " …" : ""} (factures Lodgify : à joindre depuis « Commentaires opérations » ; notes .txt Airbnb/Booking)`, { duration: 6000 });
    if (!nouveaux.length) {
      if (auto) return;
      setInfo({ ...stamp, importes: 0 }); localStorage.setItem(INFO_KEY, JSON.stringify({ ...stamp, importes: 0 }));
      if (!files.some((e) => lisible(e.f.name))) toast.error(`Dossier « ${nom} » : aucun fichier CSV, Excel ou PDF trouvé dans ce dossier ni dans ses sous-dossiers.`, { duration: 8000 });
      else toast.info(`Dossier « ${nom} » : aucun nouveau fichier. Pour renvoyer tous les fichiers et corriger les logements, utilisez « Tout réimporter ».`, { duration: 8000 });
      return;
    }
    enCours.current = true;
    setBusy(true);
    if (force) toast.dismiss();
    const attente = force ? toast.loading(`Réimport de ${nouveaux.length} fichier(s) du dossier « ${nom} » en cours…`) : null;
    const avant = force ? (await api.get("/logements/instantane").catch(() => ({ data: null }))).data : null;
    let deplaces = null;
    let ok = 0, lignes = 0;
    const resultats = [], ids = [];
    for (const e of nouveaux) {
      const { f, chemin } = e;
      const type = typeDe(chemin);
      const fd = new FormData();
      fd.append("type", type);
      fd.append("logement", type === "banque" ? "" : logementDe(chemin));
      fd.append("file", f);
      try {
        const { data } = await postWithRetry(fd);
        ok += 1; lignes += data.inserted; done.add(sig(e));
        if (aControler(data)) resultats.push({ fichier: chemin, ...data });
        if ((type === "airbnb" || type === "booking") && data.import_id) ids.push(data.import_id);
      } catch (err) { toast.error(`${chemin} (${LABEL[type]}) : ${errMsg(err)}`); }
    }
    localStorage.setItem(DONE_KEY, JSON.stringify([...done]));
    const next = { ...stamp, importes: ok, force };
    setInfo(next); localStorage.setItem(INFO_KEY, JSON.stringify(next));
    try {
      const { data } = await api.post("/rapprochement/auto");
      toast.success(`${ok} fichier(s) importé(s) · ${lignes} ligne(s) · ${data.propositions} correspondance(s) proposée(s)${data.factures ? ` · ${data.factures} facture(s) Booking` : ""}`);
      if (force) {
        await api.post("/logements/reaffecter");
        if (avant) {
          deplaces = (await api.post("/reimports", { avant, dossier: nom })).data;
          toast.success(deplaces.lignes.length ? `Logements vérifiés : ${deplaces.lignes.length} réservation(s) et leur dépôt déplacés vers le bon logement` : "Logements vérifiés : tout est déjà au bon logement");
        }
      }
      if (data.periode) {
        const ecrits = await saveRapports(data.periode, dirHandle);
        toast.success(ecrits ? `Rapport PDF et CSV enregistrés dans « ${nom}/Rapports/${data.periode.debut.slice(0, 4)} »` : "Rapport PDF et CSV téléchargés (dossier Téléchargements)");
        if (ecrits) await rangerRapports(dirHandle);
      }
    } catch (e) { toast.error(errMsg(e)); }
    if (attente) toast.dismiss(attente);
    setControles(resultats);
    setBusy(false);
    enCours.current = false;
    onDoneRef.current(ids, deplaces);
  }, []);

  const scanHandle = useCallback(async (h, force = false, auto = false) => {
    await rangerRapports(h);
    return importer(await filesFromHandle(h), h.name, h, force, auto);
  }, [importer]);

  useEffect(() => {
    if (autoFait.current || !("showDirectoryPicker" in window)) return;
    autoFait.current = true;
    getHandle().then(async (h) => {
      if (!h) return;
      setHandle(h);
      if ((await h.queryPermission({ mode: "readwrite" })) === "granted") scanHandle(h, false, true);
      else setNeedsPermission(true);
    });
  }, [scanHandle]);

  const choisir = async (force = false) => {
    forceRef.current = force;
    if (force && handle && !needsPermission) return scanHandle(handle, true);
    if ("showDirectoryPicker" in window) {
      try {
        const h = await window.showDirectoryPicker({ mode: "readwrite" });
        await saveHandle(h); setHandle(h); setNeedsPermission(false);
        return scanHandle(h, force);
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    inputRef.current.click();
  };
  const autoriser = async () => {
    if ((await handle.requestPermission({ mode: "readwrite" })) === "granted") { setNeedsPermission(false); scanHandle(handle); }
  };
  const onPicked = (e) => {
    const files = Array.from(e.target.files || []).map((f) => ({ f, chemin: f.webkitRelativePath ? f.webkitRelativePath.split("/").slice(1).join("/") : f.name }))
      .filter(({ chemin }) => !chemin.startsWith("Rapports/"));
    const nom = e.target.files?.[0]?.webkitRelativePath?.split("/")[0] || "dossier";
    e.target.value = "";
    if (files.length) importer(files, nom, null, forceRef.current);
  };

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 sm:flex-row sm:items-center sm:justify-between rise" data-testid="folder-import">
      <div className="flex items-start gap-3">
        <FolderSync className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
        <div>
          <p className="text-sm font-semibold">Importer depuis un dossier de l'ordinateur</p>
          <p className="text-xs text-slate-600" data-testid="folder-import-info">
            {info ? <>Dossier « <b>{info.nom}</b> » · dernier passage le {new Date(info.date).toLocaleString("fr-CA")} · {info.force ? `${info.importes} fichier(s) réimporté(s) sur ${info.fichiers}` : `${info.importes} nouveau(x) fichier(s) sur ${info.fichiers}`}</>
              : "Type reconnu d'après le nom du fichier, sinon celui de son sous-dossier : « Airbnb », « Booking », sinon relevé bancaire. EST / OUEST lu de la même façon. Les sous-dossiers sont lus. Les fichiers déjà importés sont ignorés."}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        {needsPermission && handle && (
          <Button variant="outline" className="bg-white" onClick={autoriser} disabled={busy} data-testid="folder-import-allow">Autoriser « {handle.name} »</Button>
        )}
        <Button onClick={() => choisir(false)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="folder-import-button">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderOpen className="h-4 w-4 mr-2" />}{info ? "Importer le dossier" : "Choisir le dossier"}
        </Button>
        {info && (
          <Button variant="outline" onClick={() => choisir(true)} disabled={busy} className="bg-white" title="Renvoie tous les fichiers du dossier, même déjà importés : les doublons sont écartés et le logement des réservations et dépôts est corrigé" data-testid="folder-import-force-button">
            <RefreshCw className="h-4 w-4 mr-2" />Tout réimporter
          </Button>
        )}
        <input ref={inputRef} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={onPicked} data-testid="folder-import-input" />
      </div>
      <ImportControleDialog resultats={controles} onClose={() => setControles([])} onChange={() => onDoneRef.current()} />
    </div>
  );
};
